import React, { useCallback } from 'react';
import { SavedPaymentMethod } from 'store/payment/types';
import { formatSavedPaymentMethodLabel } from 'utils/formatSavedPaymentMethodLabel';
import { getCartErrorMessage } from 'utils/getCartErrorMessage';
import { getCreditCycleSubtitle, getCreditsAssignedUntilSubtitle } from 'utils/getCreditCycleSubtitle';
import { CreditCycle } from 'store/price/types';
import { INVOICE, APPLE_PAY, CREDITS } from 'shared/consts/payment-method-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { selectSubmitCartError } from 'store/cart/selectors';
import { RadioOption } from '@hqo/react-components-library/dist/molecules/radioForm';
import { ApplePayIcon, Icon } from 'components/payment/quick-checkout/components/payment-methods-selector/styles';
import { faApplePay } from '@fortawesome/free-brands-svg-icons/faApplePay';
import { faCreditCard } from '@fortawesome/pro-regular-svg-icons/faCreditCard';
import { faCoins } from '@fortawesome/pro-regular-svg-icons/faCoins';

interface UsePaymentLabelsReturnValues {
  buildSavedPaymentLabels: () => RadioOption[];
}

interface UsePaymentLabelsProps {
  paymentMethods: Array<SavedPaymentMethod>;
  errorPaymentId: number;
  total: number;
  isApplePaySupported: boolean;
  creditPrice?: number;
  availableCredit?: number;
  creditCycle?: CreditCycle;
  creditsAssignedUntil?: string;
}

export const usePaymentLabels = ({
  paymentMethods,
  errorPaymentId,
  total,
  isApplePaySupported,
  creditPrice,
  availableCredit,
  creditCycle,
  creditsAssignedUntil,
}: UsePaymentLabelsProps): UsePaymentLabelsReturnValues => {
  const intl = useIntl();
  const cartError = useSelector(selectSubmitCartError);

  const buildSavedPaymentLabel = useCallback(
    (paymentMethod: SavedPaymentMethod) => {
      if (paymentMethod.payment_method_type === APPLE_PAY) {
        return intl.formatMessage({ id: 'apple_pay' });
      }
      if (paymentMethod.payment_method_type === INVOICE) {
        return intl.formatMessage({ id: 'invoice' });
      }
      if (paymentMethod.payment_method_type === CREDITS) {
        return intl.formatMessage({ id: 'pay_with_credits_generic' }, { credits: creditPrice });
      }
      const { capitalizedBrand, formattedExpMonth, formattedExpYear } = formatSavedPaymentMethodLabel(paymentMethod);

      return intl.formatMessage(
        {
          id: 'saved_payment_method_option',
        },
        {
          cardBrand: capitalizedBrand,
          cardLastFour: paymentMethod.card_last_four,
          cardExpMonth: formattedExpMonth,
          cardExpYear: formattedExpYear,
        },
      );
    },
    [intl, creditPrice],
  );

  const getPaymentMethodIcon = useCallback((paymentMethod: SavedPaymentMethod) => {
    if (paymentMethod.payment_method_type === APPLE_PAY) {
      return <ApplePayIcon icon={faApplePay} size="2x" />;
    }
    if (paymentMethod.payment_method_type === CREDITS) {
      return <Icon icon={faCoins} />;
    }
    return <Icon icon={faCreditCard} />;
  }, []);

  const buildSubtitle = useCallback(
    (paymentMethod: SavedPaymentMethod) => {
      if (paymentMethod.payment_method_type === INVOICE) {
        return intl.formatMessage({ id: 'invoice_text' });
      }
      if (paymentMethod.payment_method_type === CREDITS && creditPrice != null) {
        const balance = availableCredit ?? paymentMethod.points_balance;
        if (balance != null && creditsAssignedUntil && balance < creditPrice) {
          return getCreditsAssignedUntilSubtitle(creditsAssignedUntil, balance, intl);
        }
        if (balance != null && creditCycle) {
          return getCreditCycleSubtitle(creditCycle, balance >= creditPrice, balance, intl);
        }
        if (balance != null && balance < creditPrice) {
          return intl.formatMessage({ id: 'not_enough_credits_subtitle' }, { credits: balance });
        }
        if (balance != null) {
          const remainingCredits = balance - creditPrice;
          return intl.formatMessage({ id: 'credits_remaining_after_purchase' }, { credits: remainingCredits });
        }
      }
      return undefined;
    },
    [intl, creditPrice, availableCredit, creditCycle, creditsAssignedUntil],
  );

  const buildSavedPaymentLabels = useCallback(() => {
    return paymentMethods
      ?.map(paymentMethod => {
        const errorMessage = intl.formatMessage({ id: getCartErrorMessage(cartError) });
        const isCreditsMethod = paymentMethod?.payment_method_type === CREDITS;
        const creditsBalance = isCreditsMethod
          ? availableCredit ?? paymentMethod?.points_balance
          : paymentMethod?.points_balance;
        const notEnoughCredits = isCreditsMethod ? creditsBalance < (creditPrice || 0) : creditsBalance < total;

        return {
          label: buildSavedPaymentLabel(paymentMethod),
          subtitle: buildSubtitle(paymentMethod),
          value: paymentMethod?.id.toString(),
          canRemoveOption:
            paymentMethod?.payment_method_type !== INVOICE &&
            paymentMethod?.payment_method_type !== APPLE_PAY &&
            paymentMethod?.payment_method_type !== CREDITS,
          errorMessage:
            paymentMethod?.id === errorPaymentId && !isApplePaySupported && !isCreditsMethod ? errorMessage : undefined,
          disabled: notEnoughCredits,
          icon: getPaymentMethodIcon(paymentMethod),
        };
      })
      .reverse();
  }, [
    paymentMethods,
    intl,
    cartError,
    total,
    creditPrice,
    availableCredit,
    errorPaymentId,
    isApplePaySupported,
    getPaymentMethodIcon,
    buildSavedPaymentLabel,
    buildSubtitle,
  ]);

  return {
    buildSavedPaymentLabels,
  };
};
